// ./src/utils/reorder.ts
//
// ONE drag-to-reorder rule for lists ordered by a numeric `orderNo` (custom questions on their page and in the create
// wizard — owner, 2026-09-30: "I'd like also to re-order custom questions too"). The moved row takes the MIDPOINT between
// its new neighbours, so one write moves it; when there is no room left between them, the whole list is renumbered
// 1000, 2000, … . Pure (no imports), so `check-event-setup` runs it.

export interface OrderedRow {
  id?: string | null;
  orderNo?: number | null;
}

export interface ReorderResult<T> {
  /** The list in its new order — show it at once, while the writes go out. */
  next: T[];
  /** The `orderNo` writes that store it (one, or the whole list when renumbered). */
  updates: Array<{ id: string; orderNo: number }>;
}

/** Move the row at `from` to `to`. Nothing to write for a drop in place, or a row that has no id yet. */
export const reorderByDrag = <T extends OrderedRow>(list: readonly T[], from: number, to: number): ReorderResult<T> => {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) {
    return { next: [...list], updates: [] };
  }

  const next = [...list];
  const [moved] = next.splice(from, 1);

  next.splice(to, 0, moved);

  if (!moved.id) {
    return { next, updates: [] };
  }

  // ⚠ TIED ROWS ARE RENUMBERED, all of them. Rows that share an `orderNo` (every row created without one sits on the
  //   column default) are ordered by somebody's TIE-BREAK, and one midpoint write leaves the rest tied — so the host's
  //   drag would hold on their screen and not on the page that sorts the ties differently (review, 2026-10-01: partner
  //   packages). Writing the whole list pins the order the host sees.
  const numbers = next.map((row) => Number(row.orderNo ?? 0));

  if (new Set(numbers).size < numbers.length) {
    return { next, updates: next.filter((row) => !!row.id).map((row, i) => ({ id: row.id as string, orderNo: (i + 1) * 1000 })) };
  }

  const prevNo = to > 0 ? Number(next[to - 1].orderNo ?? 0) : 0;
  const after = next[to + 1];
  const nextNo = after ? Number(after.orderNo ?? 0) : prevNo + 2000;

  if (nextNo - prevNo > 0.001) {
    return { next, updates: [{ id: moved.id, orderNo: prevNo + (nextNo - prevNo) / 2 }] };
  }

  return { next, updates: next.filter((row) => !!row.id).map((row, i) => ({ id: row.id as string, orderNo: (i + 1) * 1000 })) };
};
